import * as React from 'react';
import { CheckboxPropagateStrategy, Tree as TreeNative, TreeExpandOnStart, TreeNode } from '../ui-native/Tree';
import { observer } from 'mobx-react';
import { ITreeCustomizeOptions } from '@shared/ui-react/TreeToolbar';
import { makeObservable, observable } from 'mobx';
import { IContextMenuItem } from '@shared/ui-react/ContextMenu';


@observer
export class Tree extends React.Component<
    {
        selectedId?: number;
        selectedUuid?: string;
        onSelect?: (node: TreeNode) => void;
        selectFallbackToRoot?: boolean;

        checkedIds?: number[];
        checkedUuids?: string[];
        onChecked?: (node: TreeNative, allChanged: TreeNative[]) => void;
        propagateCheckbox?: CheckboxPropagateStrategy;

        onRender?: (node: TreeNative) => void;
        treeData?: any;
        includesFiles: boolean;
        showRoot?: boolean;
        childFilter?: (childData: any) => boolean;
        selectionCheckbox?: boolean;

        headerControls?: boolean;
        headerControlsFilterOnly?: boolean;
        hideEmptyFolders?: boolean;
        hideEmptyFoldersAlways?: boolean;
        hideNonPresentable?: boolean;
        showNavigation?: boolean;
        hideExpandOptions?: boolean;
        expandOnStart?: TreeExpandOnStart;
        filterTreePlaceholder?: string;
        matchesFilterFn?: (node: TreeNative, filterText: string) => boolean;

        onDblClick?: (node: TreeNative) => void;
        onContextMenu?: (node: TreeNative) => IContextMenuItem[];
        showContextMenu?: boolean;
        showContextMenuCreateFolder?: boolean;

        storageKey?: string;
        storageIsGlobal?: boolean;
        fetchContentCountsFn?: () => Promise<any>;
        onTreeInitialized?: (tree: TreeNative) => void;
        sortChildren?: boolean;

        toolbarExtra?: JSX.Element;
    } & ITreeCustomizeOptions
> {
    @observable.ref public tree: TreeNative = null;

    public el: HTMLDivElement = null;

    static defaultProps = {
        onRender: () => {
            /* Default to empty function */
        },
        showRoot: true,
        includesFiles: false,
        headerControls: false,
        headerControlsFilterOnly: false,
        hideEmptyFolders: false,
        hideEmptyFoldersAlways: false,
        hideNonPresentable: false,
        showNavigation: false,
        hideExpandOptions: false,
        showContextMenu: true,
        showContextMenuCreateFolder: false,
        propagateCheckbox: 'deep',
        childFilter: null,
        sortChildren: true,
        expandOnStart: 'firstLevel',
    };

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.initTree();
    }

    componentWillUnmount() {
        this.tree?.destroy();
    }

    componentDidUpdate(prevProps) {
        if (this.tree && this.props.treeData != prevProps.treeData) {
            setTimeout(() => {
                this.initTree();
            }, 50);
        }

        if (this.tree && this.props.selectedId != prevProps.selectedId) {
            this.tree.selectById(this.props.selectedId, this.props.selectFallbackToRoot);
        }

        if (this.tree && this.props.selectedUuid != prevProps.selectedUuid) {
            this.tree.selectByUuid(this.props.selectedUuid, this.props.selectFallbackToRoot);
        }

        if (this.tree && this.props.checkedIds != prevProps.checkedIds) {
            this.tree.checkIds(this.props.checkedIds, true, true);
        }

        if (this.tree && this.props.checkedUuids != prevProps.checkedUuids) {
            this.tree.checkUuids(this.props.checkedUuids, true, true, true);
        }

        if (this.tree && this.props.fetchContentCountsFn != prevProps.fetchContentCountsFn) {
            this.tree.fetchContentCountsFn = this.props.fetchContentCountsFn;
        }
    }

    render() {
        return (
            <>
                <div ref={el => (this.el = el)} className='tree-react-wrapper' style={{ overflow: 'auto' }}></div>
            </>
        );
    }

    initTree = () => {
        if (this.tree) {
            this.tree.destroy();
        }

        const tree = new TreeNative({
            rootEl: $(this.el),
            showRoot: this.props.showRoot,
            childFilter: this.props.childFilter,
            showCheckboxes: !!this.props.onChecked,
            selectionCheckbox: this.props.selectionCheckbox,
            matchesFilterFn: this.props.matchesFilterFn,
            onContextMenu: this.props.onContextMenu,
            showContextMenu: this.props.showContextMenu,
            showContextMenuCreateFolder: this.props.showContextMenuCreateFolder,
            hideNonPresentable: this.props.hideNonPresentable,
            propagateCheckbox: this.props.propagateCheckbox,
            storageKey: this.props.storageKey,
            storageIsGlobal: this.props.storageIsGlobal,
            expandOnStart: this.props.expandOnStart,
            hideEmptyFolders: this.props.hideEmptyFolders,
            hideEmptyFoldersAlways: this.props.hideEmptyFoldersAlways,
            fetchContentCountsFn: this.props.fetchContentCountsFn,
            checkOnHighlightSelected: !!this.props.onChecked,
            sortChildren: this.props.sortChildren,
            toolbar: {
                show: this.props.headerControls,
                filterOnly: this.props.headerControlsFilterOnly,
                filterTreePlaceholder: this.props.filterTreePlaceholder,
                showNavigation: this.props.showNavigation,
                hideExpandOptions: this.props.hideExpandOptions,
            },
        });

        tree.on('render', () => {
            // handle select
            if (typeof this.props.onSelect === 'function') {
                if (this.props.selectedId) {
                    const actualSelected = tree.selectById(this.props.selectedId, this.props.selectFallbackToRoot);
                    if (actualSelected && actualSelected.id != this.props.selectedId) {
                        // dont use === here, id can be number (legacy) or string
                        this.props.onSelect(actualSelected);
                    }
                } else if (this.props.selectedUuid) {
                    const actualSelected = tree.selectByUuid(this.props.selectedUuid, this.props.selectFallbackToRoot);
                    if (actualSelected && actualSelected.uuid != this.props.selectedUuid) {
                        this.props.onSelect(actualSelected);
                    }
                }

                tree.on('select', (e, data) => {
                    this.props.onSelect(data.node);
                });

                if (this.props.onDblClick) {
                    tree.on('dblclick', (e, data) => {
                        this.props.onDblClick(data);
                    });
                }
            }

            // handle checkbox
            if (typeof this.props.onChecked === 'function') {
                if (this.props.checkedIds) {
                    tree.checkIds(this.props.checkedIds, true, true);
                } else if (this.props.checkedUuids) {
                    tree.checkUuids(this.props.checkedUuids, true, true, true);
                }

                tree.on('check', (e, data) => {
                    typeof this.props.onChecked(data.node, data.changed);
                });
            }

            this.props.onRender(tree);

            this.tree = tree;

            if (this.props.onTreeInitialized) {
                this.props.onTreeInitialized(tree);
            }
        });

        if (this.props.treeData) {
            tree.initAndRender(this.props.treeData, this.props.includesFiles);
        } else {
            tree.fetch();
        }
    };
}
